import type { LeaderboardMenuItem } from '../../components/Leaderboard/LeaderboardMenu/LeaderboardMenu.types';
import type { LeaderboardVariant } from '../../components/Profile/Leaderboard/List/List.types';

import { Card, Flex, Tabs } from '@rango-dev/whale';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { MarketingCampaign } from '../../components/Campaign/MarketingCampaign/MarketingCampaign';
import { LeaderboardList } from '../../components/Leaderboard/LeaderboardList';
import { getLeaderboardMenu } from '../../components/Leaderboard/LeaderboardMenu/LeaderboardMenu';
import { LeaderboardSidebar } from '../../components/Leaderboard/LeaderboardSidebar';
import { LeaderboardTopContent } from '../../components/Leaderboard/LeaderboardSidebar/LeaderboardTopContent';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import { fetchAllDynamicCampaign } from '../../state/CampaignsSlice/thunks';
import { AllTimeLeaderboardTab, LeaderboardViewType } from '../../types';
import { getCampaignMenuItem } from '../../utils/campaign';

import styles from './Leaderboard.module.css';

export function Leaderboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isTablet, isMobile } = useWindowDimensions();

  const dispatch = useAppDispatch();
  const isSmall = isTablet || isMobile;

  const { type: slug = AllTimeLeaderboardTab.SCORE } = useParams<{ type: string }>();
  const dynamicCampaigns = useAppSelector((state) => state.campaign.campaigns.data);

  /*
   * Derive the category from the slug alone (not the loaded campaign list), so a direct
   * campaign URL selects the Campaigns tab immediately instead of flashing All Time until
   * the campaigns finish loading. Any slug that is not an All Time sub-tab is a campaign.
   */
  const isAllTimeTab = Object.values(AllTimeLeaderboardTab).includes(slug as AllTimeLeaderboardTab);
  const campaignId = isAllTimeTab ? undefined : slug;
  const activeCategory = isAllTimeTab ? LeaderboardViewType.ALL_TIME : LeaderboardViewType.CAMPAIGN;

  const leaderboardMenu = getLeaderboardMenu();

  const firstCampaignSlug = dynamicCampaigns?.[0]?.campaignId;

  const categoryTabs = [
    { id: LeaderboardViewType.ALL_TIME, title: leaderboardMenu.title },
    { id: LeaderboardViewType.CAMPAIGN, title: t('Campaigns') },
  ];

  const subcategoryTabs =
    activeCategory === LeaderboardViewType.ALL_TIME
      ? Object.values(leaderboardMenu.menu).map((item) => ({
          id: item.id,
          title: item.shortTitle,
        }))
      : [
          ...(dynamicCampaigns ?? []).map((item) => ({
            id: item.campaignId,
            title: item.title,
          })),
        ];

  let activeMenuItem: LeaderboardMenuItem | null;
  if (activeCategory === LeaderboardViewType.CAMPAIGN) {
    const dynamicCampaign = dynamicCampaigns?.find((item) => item.campaignId === slug);
    activeMenuItem = dynamicCampaign ? getCampaignMenuItem(dynamicCampaign) : null;
  } else {
    activeMenuItem = leaderboardMenu.menu[slug] ?? null;
  }

  const mainContent =
    activeCategory === LeaderboardViewType.CAMPAIGN && campaignId ? (
      <MarketingCampaign campaignId={campaignId} />
    ) : (
      <LeaderboardList type={(activeMenuItem?.id || slug) as LeaderboardVariant} />
    );

  const metaTitle = t('Rango Exchange | Leaderboard - Top Rangonatus, Scores & Rankings');
  const metaDescription = t(
    'Explore the Rango Exchange leaderboard to see the top traders, their scores, trading volumes, and rankings. View the high rank Rangonauts in the competition!',
  );

  useEffect(() => {
    const request = dispatch(fetchAllDynamicCampaign());
    return () => {
      request?.abort?.();
    };
  }, []);

  return (
    <Flex gap={{ sm: '5' }} width="100%">
      <Helmet>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />

        <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>

      {isSmall && (
        <Flex direction="column" gap="4" width="100%">
          <Tabs.Root
            className={styles.tabsRoot}
            value={activeCategory}
            onValueChange={(value) => {
              const tabSlug =
                value === LeaderboardViewType.CAMPAIGN
                  ? firstCampaignSlug
                  : leaderboardMenu.defaultActiveTab;
              if (tabSlug) {
                navigate(`/leaderboard/${tabSlug}`);
              }
            }}>
            <Tabs.List size="2" color="cyan">
              {categoryTabs.map((tab) => (
                <Tabs.Trigger className={styles.tabTrigger} key={tab.id} value={tab.id}>
                  {tab.title}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>
          <Card size={{ initial: '1', sm: '2' }} className={styles.mobileCard}>
            <Flex direction="column" gap="4">
              <LeaderboardTopContent activeTab={slug} />
              {activeMenuItem && (
                <LeaderboardSidebar
                  activeTab={activeMenuItem.id}
                  viewType={activeCategory}
                  leaderboardItem={activeMenuItem}
                  campaignId={campaignId}
                />
              )}
              <Tabs.Root value={slug} onValueChange={(value) => navigate(`/leaderboard/${value}`)}>
                <Tabs.List size="2" color="cyan">
                  {subcategoryTabs.map((tab) => (
                    <Tabs.Trigger className={styles.tabTrigger} key={tab.id} value={tab.id}>
                      {tab.title}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
              </Tabs.Root>
              {mainContent}
            </Flex>
          </Card>
        </Flex>
      )}

      {!isSmall && activeMenuItem && (
        <>
          <LeaderboardSidebar
            activeTab={activeMenuItem.id}
            viewType={activeCategory}
            leaderboardItem={activeMenuItem}
            campaignId={campaignId}
          />
          {mainContent}
        </>
      )}
    </Flex>
  );
}
